import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(utc);
// `timezone` needs `utc` extended first.
dayjs.extend(timezone);
// `z` token (zone abbreviation).
dayjs.extend(advancedFormat);

const defaultDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

/**
 * The reader's IANA timezone, e.g. "America/New_York" or "Europe/Madrid".
 * Sent to the backend so server-rendered documents show local times: the same
 * statement pulled from the US and from Spain then reads correctly in both.
 * @returns {string | undefined} - The timezone, or undefined when the browser
 * cannot resolve one, in which case the backend falls back to UTC.
 */
export function getBrowserTimezone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}

/**
 * Formats a date string to a more readable format using "en" or "es" language.
 * @param {string} date - The date string to format.
 * @param language - the language string for formatting (e.g., 'en', 'es').
 * @returns {string} - The formatted date string.
 */
export function formatDateString(date: string, language: string): string {
  return new Date(date)
    .toLocaleDateString(
      language === "en" ? "en-US" : "es-ES",
      defaultDateFormatOptions,
    )
    .replace(",", "");
}

/**
 * Converts a Unix timestamp in seconds to an ISO string.
 * @param {number} timestamp - The Unix timestamp in seconds.
 * @returns {string} - The ISO string representation of the date.
 */
export function convertUnixTimestampToISOString(timestamp: number): string {
  return new Date(timestamp * 1000).toISOString();
}

/**
 * Splits an instant into the date and time lines of a history table cell,
 * same formats as the statement (TA-5141): `5 Apr 2026` / `6:55 PM EDT`.
 * @param value - The instant to format.
 * @param timeZone - IANA zone; defaults to the browser's.
 * @returns {{ date: string; time?: string }} - `date` is "-" when invalid.
 */
export function formatDateTimeLines(
  value: Date | string | undefined,
  timeZone: string | undefined = getBrowserTimezone(),
): { date: string; time?: string } {
  if (!value || !dayjs(value).isValid()) return { date: "-" };
  const instant = timeZone ? dayjs.utc(value).tz(timeZone) : dayjs(value);
  return {
    date: instant.format("D MMM YYYY"),
    time: instant.format("h:mm A z"),
  };
}
